import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const READY_TONE = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
  slate: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_TONE = {
  satisfied: "bg-emerald-100 text-emerald-700", overridden: "bg-violet-100 text-violet-700",
  not_applicable: "bg-slate-100 text-slate-500", pending: "bg-amber-100 text-amber-700",
  missing: "bg-rose-100 text-rose-700", not_evaluated: "bg-slate-100 text-slate-400",
};
const STATUS_LABEL = { not_evaluated: "Not Evaluated", missing: "Missing", pending: "Pending Review", satisfied: "Satisfied", overridden: "Overridden", not_applicable: "Not Applicable" };
const DOC_STATE = {
  missing: { label: "Missing", cls: "bg-rose-100 text-rose-700" },
  uploaded: { label: "Uploaded", cls: "bg-blue-100 text-blue-700" },
  verified: { label: "Verified", cls: "bg-emerald-100 text-emerald-700" },
  waived: { label: "Waived", cls: "bg-violet-100 text-violet-700" },
};
const docBadge = (dep) => {
  if (dep.dependency_type !== "document") return null;
  const state = dep.status === "overridden" ? "waived" : (dep.doc_state || (dep.status === "satisfied" ? "verified" : dep.status === "pending" ? "uploaded" : "missing"));
  return DOC_STATE[state] || null;
};
const TYPE_ICON = { service: "GitBranch", document: "FileText", information: "ClipboardList" };

/* ───────────────── "Why is this required?" side panel ───────────────── */
function WhyPanel({ engagementId, depId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/dependency-runtime/engagements/${engagementId}/dependencies/${depId}/why`).then((r) => setD(r.data)).catch(() => setD(false)); }, [engagementId, depId]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="dep-why-panel">
      <div className="w-full max-w-md bg-white h-full overflow-y-auto tf-scroll shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><Icons.HelpCircle className="h-5 w-5 text-blue-600" /><h3 className="font-heading font-semibold text-slate-900">Why is this required?</h3></div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded text-slate-500"><Icons.X className="h-4 w-4" /></button>
        </div>
        {d === null ? <div className="py-12 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500 inline" /></div> :
         d === false ? <p className="p-5 text-sm text-slate-400">Could not load.</p> : (
          <div className="p-5 space-y-4 text-sm">
            <div><div className="text-lg font-semibold text-slate-900">{d.target_label}</div>
              <div className="text-xs text-slate-500 mt-0.5 capitalize">{d.dependency_type} · {(d.requirement_level || "").replace(/_/g, " ")} · blocks {(d.blocking_level || "").replace(/_/g, " ")}</div></div>
            <Section title="Internal Explanation">{d.internal_explanation || <em className="text-slate-400">None provided.</em>}</Section>
            <Section title="Client Explanation">{d.client_explanation || <em className="text-slate-400">None provided.</em>}</Section>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Authoritative References</div>
              {(d.references || []).length === 0 && !d.reference_note ? <p className="text-slate-400 text-xs">No references linked.</p> : (
                <div className="space-y-1.5">
                  {(d.references || []).map((r, i) => (
                    <div key={i} className="flex items-start gap-2 border border-slate-100 rounded-lg px-3 py-2" data-testid={`dep-why-ref-${i}`}>
                      {r.ref_type === "knowledge_collection" ? <Icons.Library className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" /> : <Icons.BookOpen className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />}
                      <div className="min-w-0"><div className="text-slate-700 font-medium">{r.label}</div>
                        {r.description && <div className="text-xs text-slate-400">{r.description}</div>}
                        {r.note && <div className="text-xs text-slate-400">{r.note}</div>}
                        {r.ref_type === "knowledge_collection" && !r.available && <div className="text-[11px] text-rose-400">Collection no longer available</div>}
                      </div>
                    </div>
                  ))}
                  {d.reference_note && <div className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2 italic">{d.reference_note}</div>}
                </div>
              )}
            </div>
            <Section title="Dependency Source">
              {d.source?.type === "template" ? <>Package: <strong>{d.source.package}</strong> · v{d.source.version}</> : "Service-specific"}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
const Section = ({ title, children }) => (
  <div><div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{title}</div><div className="text-slate-700 whitespace-pre-wrap">{children}</div></div>
);

/* ───────────────── override inline form ───────────────── */
function OverrideForm({ onSubmit, onCancel }) {
  const [reason, setReason] = useState("");
  const [temporary, setTemporary] = useState(false);
  return (
    <div className="mt-2 p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-2" data-testid="dep-override-form">
      <textarea className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" rows={2} placeholder="Override reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dep-override-reason" />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={temporary} onChange={(e) => setTemporary(e.target.checked)} className="accent-violet-600" />Temporary override</label>
        <div className="flex gap-2">
          <button onClick={onCancel} className="text-xs text-slate-500 px-2">Cancel</button>
          <button onClick={() => reason.trim() ? onSubmit({ reason, temporary }) : toast.error("Reason required")} data-testid="dep-override-submit" className="text-xs bg-violet-600 text-white rounded px-3 py-1 font-medium">Confirm Override</button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────── dependency row ───────────────── */
function DepRow({ eid, dep, canOverride, statusOptions, onChanged }) {
  const Ic = Icons[TYPE_ICON[dep.dependency_type]] || Icons.Circle;
  const [why, setWhy] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [busy, setBusy] = useState(false);

  const setStatus = async (status) => {
    setBusy(true);
    try { const r = await api.post(`/dependency-runtime/engagements/${eid}/dependencies/${dep.dep_id}/status`, { status }); onChanged(r.data.readiness); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const doOverride = async ({ reason, temporary }) => {
    setBusy(true);
    try { const r = await api.post(`/dependency-runtime/engagements/${eid}/dependencies/${dep.dep_id}/override`, { reason, temporary, stage: dep.blocking_level }); setOverriding(false); onChanged(r.data.readiness); toast.success("Overridden"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  // Indicate if this dep cross-satisfies a prior-year unit
  const isPriorYear = dep.target_key === "prior-year-return" && dep.referenced_tax_year;

  return (
    <div className="border border-slate-200 rounded-lg px-3 py-2.5 bg-white" data-testid={`dep-runtime-row-${dep.dep_id}`}>
      <div className="flex items-center gap-2.5">
        <span className="h-7 w-7 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><Ic className="h-3.5 w-3.5" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-800 truncate">{dep.target_label}</span>
            {(() => { const db = docBadge(dep); return db
              ? <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${db.cls}`} data-testid={`doc-state-${dep.dep_id}`}>{db.label}</span>
              : <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_TONE[dep.status] || "bg-slate-100 text-slate-500"}`}>{STATUS_LABEL[dep.status]}</span>; })()}
            {dep.source === "template" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600" title={dep.template_name}>Package</span>}
            {isPriorYear && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600" data-testid={`dep-prior-year-badge-${dep.dep_id}`}>Prior Year {dep.referenced_tax_year}</span>}
          </div>
          <div className="text-[11px] text-slate-400 truncate">{dep.service_name} · blocks {(dep.blocking_level || "").replace(/_/g, " ")}{dep.override?.reason && ` · override: ${dep.override.reason}`}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <select disabled={busy} value={["overridden"].includes(dep.status) ? "" : dep.status} onChange={(e) => e.target.value && setStatus(e.target.value)} data-testid={`dep-status-select-${dep.dep_id}`}
            className="border border-slate-300 rounded px-1.5 py-1 text-xs text-slate-600">
            {dep.status === "overridden" && <option value="">Overridden</option>}
            {(statusOptions || []).filter((s) => s.value !== "overridden").map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {canOverride && dep.status !== "overridden" && <button onClick={() => setOverriding((s) => !s)} title="Override" data-testid={`dep-override-btn-${dep.dep_id}`} className="p-1.5 hover:bg-violet-50 rounded text-violet-500"><Icons.ShieldAlert className="h-4 w-4" /></button>}
          <button onClick={() => setWhy(true)} title="Why is this required?" data-testid={`dep-why-btn-${dep.dep_id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-400"><Icons.HelpCircle className="h-4 w-4" /></button>
        </div>
      </div>
      {overriding && <OverrideForm onSubmit={doOverride} onCancel={() => setOverriding(false)} />}
      {why && <WhyPanel engagementId={eid} depId={dep.dep_id} onClose={() => setWhy(false)} />}
    </div>
  );
}

/* ───────────────── group deps by Tax Year / Production Unit ───────────────── */
function groupDependencies(deps) {
  const yearLinked = {};    // key: "YYYY||serviceCode"  value: { label, year, deps[] }
  const nonYearSpecific = [];
  const orphaned = [];      // explicitly backend-flagged integrity exception

  for (const dep of deps) {
    const year = dep.unit_tax_year;
    const svcCode = dep.service_code || "";

    if (dep.is_orphaned) {
      // Backend explicitly flagged this as a relationship integrity exception.
      // unit_id missing alone does NOT trigger orphaned state — that would incorrectly
      // flag engagements that simply don't have Production Units yet.
      orphaned.push(dep);
    } else if (year) {
      // Year-specific — group by year regardless of whether unit_id is resolved.
      // unit_id may be null for engagements that don't have Production Units yet;
      // that is not an integrity error, just an unlinked state.
      const gKey = `${year}||${svcCode}`;
      if (!yearLinked[gKey]) {
        yearLinked[gKey] = {
          groupKey: gKey,
          year,
          serviceCode: svcCode,
          serviceName: dep.service_name || svcCode,
          unitId: dep.unit_id,
          deps: [],
        };
      }
      yearLinked[gKey].deps.push(dep);
    } else {
      // Non-year-specific service — legitimate general requirement
      nonYearSpecific.push(dep);
    }
  }

  // Sort year groups: newest year first
  const yearGroups = Object.values(yearLinked).sort((a, b) => Number(b.year) - Number(a.year));

  return { yearGroups, nonYearSpecific, orphaned };
}

/* ───────────────── year/unit section ───────────────── */
function UnitSection({ year, serviceName, unitId, deps, eid, canOverride, statusOptions, onChanged }) {
  return (
    <div data-testid={`dep-unit-section-${year}`}>
      <div className="flex items-center gap-2 mb-2 mt-3" data-testid={`dep-unit-header-${year}`}>
        <Icons.Calendar className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{year} — {serviceName}</span>
        <span className="text-[10px] text-slate-400">{deps.length} requirement{deps.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="space-y-1.5 ml-1">
        {deps.map((dep) => <DepRow key={dep.dep_id} eid={eid} dep={dep} canOverride={canOverride} statusOptions={statusOptions} onChanged={onChanged} />)}
      </div>
    </div>
  );
}

/* ───────────────── full dashboard ───────────────── */
export function DependencyDashboard({ engagementId, defaultOpen = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(defaultOpen);
  const load = useCallback(() => api.get(`/dependency-runtime/engagements/${engagementId}`).then((r) => setData(r.data)).catch(() => setData(false)), [engagementId]);
  useEffect(() => { load(); }, [load]);
  const onChanged = (readiness) => { setData((p) => (p ? { ...p, readiness } : p)); load(); };

  if (data === false) return null;
  if (!data) return <div className="py-6 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div>;
  const r = data.readiness;

  const { yearGroups, nonYearSpecific, orphaned } = groupDependencies(data.dependencies || []);
  const hasOrphans = orphaned.length > 0;

  // Header summary from backend (authoritative): "X satisfied · Y not applicable · Z blocking"
  const headerSummary = r.summary || (() => {
    const c = r.counts;
    return `${(r.satisfied_count ?? (c.satisfied + c.overridden))} satisfied · ${(r.not_applicable_count ?? c.not_applicable)} not applicable · ${(r.blocking_count ?? c.blocking_outstanding)} blocking`;
  })();

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="dep-dashboard">
      <button onClick={() => setOpen((s) => !s)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-testid="dep-dashboard-toggle">
        <div className="flex items-center gap-3">
          <Icons.Network className="h-4 w-4 text-blue-500" />
          <span className="font-heading font-semibold text-slate-800 text-sm">Dependencies</span>
          <span className="text-xs text-slate-400" data-testid="dep-readiness-summary">{headerSummary}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${READY_TONE[r.color] || READY_TONE.slate}`} data-testid="dep-readiness-badge">{r.label}</span>
          <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100">
          {data.dependencies.length === 0 ? (
            <p className="text-sm text-slate-400 py-3">No dependencies configured for this engagement's services.</p>
          ) : (
            <>
              {/* Year-specific groups: one section per Production Unit / Tax Year */}
              {yearGroups.map((g) => (
                <UnitSection
                  key={g.groupKey}
                  year={g.year}
                  serviceName={g.serviceName}
                  unitId={g.unitId}
                  deps={g.deps}
                  eid={engagementId}
                  canOverride={data.can_override}
                  statusOptions={data.status_options}
                  onChanged={onChanged}
                />
              ))}

              {/* Non-year-specific general requirements */}
              {nonYearSpecific.length > 0 && (
                <div data-testid="dep-general-section">
                  {(yearGroups.length > 0 || hasOrphans) && (
                    <div className="flex items-center gap-2 mb-2 mt-3">
                      <Icons.List className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Other Requirements</span>
                      <span className="text-[10px] text-slate-400">{nonYearSpecific.length} requirement{nonYearSpecific.length !== 1 ? "s" : ""}</span>
                    </div>
                  )}
                  <div className="space-y-1.5 ml-1">
                    {nonYearSpecific.map((dep) => <DepRow key={dep.dep_id} eid={engagementId} dep={dep} canOverride={data.can_override} statusOptions={data.status_options} onChanged={onChanged} />)}
                  </div>
                </div>
              )}

              {/* Orphaned / Unlinked — data integrity exceptions only */}
              {hasOrphans && (
                <div className="mt-4 border border-amber-200 rounded-lg px-3 pt-2 pb-3 bg-amber-50" data-testid="dep-orphaned-section">
                  <div className="flex items-center gap-2 mb-2">
                    <Icons.AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                    <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Orphaned / Unlinked — Requires Attention</span>
                    <span className="text-[10px] text-amber-600">{orphaned.length} item{orphaned.length !== 1 ? "s" : ""}</span>
                  </div>
                  <p className="text-[11px] text-amber-600 mb-2">These dependencies belong to year-specific services but could not be linked to a Production Unit. They are included in readiness calculations.</p>
                  <div className="space-y-1.5">
                    {orphaned.map((dep) => <DepRow key={dep.dep_id} eid={engagementId} dep={dep} canOverride={data.can_override} statusOptions={data.status_options} onChanged={onChanged} />)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ───────────────── compact client-workspace card ───────────────── */
export function DependencyStatusCard({ clientId, onOpenEngagement }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/dependency-runtime/clients/${clientId}/summary`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, [clientId]);
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="dep-client-summary">
      {items.map((e) => {
        const r = e.readiness;
        const summary = r.summary || (() => {
          const c = r.counts;
          return `${r.satisfied_count ?? (c.satisfied + c.overridden)} satisfied · ${r.not_applicable_count ?? c.not_applicable} not applicable · ${r.blocking_count ?? c.blocking_outstanding} blocking`;
        })();
        return (
          <button key={e.engagement_id} onClick={() => onOpenEngagement?.(e.engagement_id)} data-testid={`dep-client-card-${e.engagement_id}`}
            className="w-full text-left bg-white rounded-xl border border-slate-200 p-3.5 hover:shadow-sm transition-shadow">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-slate-700">{e.engagement_number || "Engagement"} · Dependency Status</span>
              <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${READY_TONE[r.color] || READY_TONE.slate}`}>{r.label}</span>
            </div>
            <div className="text-xs text-slate-500" data-testid={`dep-card-summary-${e.engagement_id}`}>{summary}</div>
          </button>
        );
      })}
    </div>
  );
}

